import {
  AdminExtendedAccount,
  AdminListUserInvitationsRequestParams,
  AdminService,
  InternalUserInvitation,
} from '@admin-api/index';
import { Clipboard } from '@angular/cdk/clipboard';
import {
  AfterViewInit,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelectChange } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { fuseAnimations } from '../../../../@fuse/animations';
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_OPTIONS,
} from '../../../core/constants';
import { InviteUserDialogComponent } from '../invite-user-dialog/invite-user-dialog.component';

@Component({
  selector: 'user-invitations-list',
  templateUrl: './user-invitations-list.component.html',
  encapsulation: ViewEncapsulation.None,
  animations: fuseAnimations,
})
export class UserInvitationsListComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @ViewChild(MatPaginator) private _paginator: MatPaginator;
  @Input() accountId!: string;
  /**
   * Account type — used to filter the invite dialog's role options so we
   * don't offer roles that the backend would reject for this account type.
   * If omitted, the dialog falls back to showing all roles.
   */
  @Input() accountType?: AdminExtendedAccount.TypeEnum;

  public dataSource = new MatTableDataSource<InternalUserInvitation>();
  public total = 0;
  public isLoading = true;
  public errorMessage: string | null = null;
  public pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;

  searchInputControl: UntypedFormControl = new UntypedFormControl();
  filterByRole: InternalUserInvitation.RoleEnum | null = null;

  roleOptions: InternalUserInvitation.RoleEnum[] = Object.values(
    InternalUserInvitation.RoleEnum,
  );

  tableColumns: string[] = [
    'email',
    'role',
    'created_at',
    'expires_at',
    'status',
    'actions',
  ];

  private readonly _unsubscribeAll = new Subject<void>();

  constructor(
    private _adminService: AdminService,
    private _matDialog: MatDialog,
    private _snackBar: MatSnackBar,
    private _clipboard: Clipboard,
    private _fuseConfirmationService: FuseConfirmationService,
  ) {}

  ngOnInit(): void {
    // Server-side pagination only — don't wire MatTableDataSource.paginator,
    // which would re-slice client-side.
    this.fetchFiltered();

    this.searchInputControl.valueChanges
      .pipe(
        takeUntil(this._unsubscribeAll),
        debounceTime(300),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        if (this._paginator) {
          this._paginator.pageIndex = 0;
        }
        this.fetchFiltered();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.accountId && !changes.accountId.firstChange) {
      if (this._paginator) {
        this._paginator.pageIndex = 0;
      }
      this.fetchFiltered();
    }
  }

  ngAfterViewInit(): void {
    this._paginator.page
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => this.fetchFiltered());
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  trackByFn(index: number, item: InternalUserInvitation): string {
    return item?.id || `${index}`;
  }

  filterByRoleChanged(_change: MatSelectChange): void {
    if (this._paginator) {
      this._paginator.pageIndex = 0;
    }
    this.fetchFiltered();
  }

  isExpired(invitation: InternalUserInvitation): boolean {
    return new Date(invitation.expires_at).getTime() < Date.now();
  }

  copyUrl(invitation: InternalUserInvitation): void {
    if (!invitation.invitation_url) {
      this._snackBar.open('No invitation URL available', 'OK', {
        duration: 3000,
      });
      return;
    }
    this._clipboard.copy(invitation.invitation_url);
    this._snackBar.open('Invitation link copied', null, { duration: 2000 });
  }

  resend(invitation: InternalUserInvitation): void {
    const dialogRef = this._fuseConfirmationService.open({
      title: 'Resend invitation',
      message: `Re-send the invitation email to "${invitation.email}"? This will extend the expiration by 7 days.`,
      icon: {
        show: true,
        name: 'heroicons_outline:mail',
        color: 'primary',
      },
      actions: {
        confirm: { show: true, label: 'Resend', color: 'primary' },
        cancel: { show: true, label: 'Cancel' },
      },
      dismissible: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result !== 'confirmed') return;
      this._adminService
        .adminResendUserInvitation({ id: invitation.id })
        .subscribe({
          next: () => {
            this._snackBar.open('Invitation resent', null, { duration: 3000 });
            this.fetchFiltered();
          },
          error: (err) => this.showError(err),
        });
    });
  }

  delete(invitation: InternalUserInvitation): void {
    const dialogRef = this._fuseConfirmationService.open({
      title: 'Revoke invitation',
      message: `Revoke the invitation for "${invitation.email}"? The invitation link will no longer work.`,
      icon: {
        show: true,
        name: 'heroicons_outline:exclamation',
        color: 'warn',
      },
      actions: {
        confirm: { show: true, label: 'Revoke', color: 'warn' },
        cancel: { show: true, label: 'Cancel' },
      },
      dismissible: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result !== 'confirmed') return;
      this._adminService
        .adminDeleteUserInvitation({ id: invitation.id })
        .subscribe({
          next: () => {
            this._snackBar.open('Invitation revoked', null, { duration: 3000 });
            this.fetchFiltered();
          },
          error: (err) => this.showError(err),
        });
    });
  }

  invite(): void {
    if (!this.accountId) return;
    const dialogRef = this._matDialog.open(InviteUserDialogComponent, {
      panelClass: 'user-form-dialog',
      width: '480px',
      data: { account_id: this.accountId, account_type: this.accountType },
    });

    dialogRef.afterClosed().subscribe((params) => {
      if (!params) return;
      this._adminService
        .inviteUser({ inviteAccountUserParams: params })
        .subscribe({
          next: () => {
            this._snackBar.open('Invitation sent', null, { duration: 3000 });
            this.fetchFiltered();
          },
          error: (err) => this.showError(err),
        });
    });
  }

  private fetchFiltered(): void {
    if (!this.accountId) {
      this.dataSource.data = [];
      this.total = 0;
      this.isLoading = false;
      return;
    }

    const offset = this._paginator
      ? this._paginator.pageIndex * this._paginator.pageSize
      : 0;
    const limit = this._paginator?.pageSize || DEFAULT_PAGE_SIZE;

    const email: string = (this.searchInputControl.value ?? '').trim();
    const params: AdminListUserInvitationsRequestParams = {
      accountId: this.accountId,
      offset,
      limit,
      role: this.filterByRole || undefined,
      email: email || undefined,
    };

    this.isLoading = true;
    this.errorMessage = null;
    this._adminService.adminListUserInvitations(params).subscribe({
      next: (res) => {
        this.dataSource.data = res.items;
        this.total = res.total ?? res.items.length;
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.showError(err);
      },
    });
  }

  private showError(err: any): void {
    const message = err?.error?.message || err?.message || 'Unknown error';
    this.errorMessage = `Error: ${message}`;
    this._snackBar.open(this.errorMessage, 'OK', {
      duration: 5000,
      verticalPosition: 'top',
      panelClass: ['mat-toolbar', 'mat-warn'],
    });
  }
}
